import { createStore, reducer, createTypedHooks } from 'easy-peasy';
import type { SessionModel } from './session';
import { sessionModel } from './session';
import type { NavigationModel } from './navigation';
import { navigationModel } from './navigation';
import type { QueryInvalidationModel } from './queryInvalidation';
import { queryInvalidationModel } from './queryInvalidation';
import { createLogger } from 'redux-logger';
import type { RouterState } from 'redux-first-history';
import { createReduxHistoryContext, reachify } from 'redux-first-history';
import { createBrowserHistory } from 'history';
import { injections } from './services/injections';
import type { LoadingModel } from './loading';
import { loadingModel } from './loading';
import type { ErrorModel } from './errors';
import { errorModel } from './errors';
import type { MessagesModel } from './messages';
import { messagesModel } from './messages';
import type { HelperModel } from './helper';
import { helperModel } from './helper';
import type { SiteReportingModel } from './siteReporting';
import { siteReportingModel } from './siteReporting';
import type { FulfillmentModel } from './fulfillmentCenterConfig';
import { fulfillmentModel } from './fulfillmentCenterConfig';
import type { I18nModel } from './i18n';
import { i18nModel } from './i18n';
import type { PartnerModel } from './partner';
import { partnerModel } from './partner';
import type { UserModel } from './users';
import { userModel } from './users';
import type { ServiceStatusModel } from './serviceStatus';
import { serviceStatusModel } from './serviceStatus';
import type { FileModel } from './files';
import { fileModel } from './files';
import type { TrackingModel } from './tracking';
import { trackingModel } from './tracking';
import type { CarrierModel } from './carriers';
import { carrierModel } from './carriers';
import { checkRefreshing } from './middleware/checkRefreshing';
import type { SecurityModel } from './security';
import { securityModel } from './security';
import type { ZplModel } from './zpl';
import { zplModel } from './zpl';
import type { ShipmentsModel } from './shipments';
import { shipmentsModel } from './shipments';
import type { DataFetchingModel } from './data-fetching';
import { dataFetchingModel } from './data-fetching';
import type { DatalakeModel } from './datalake';
import { datalakeModel } from './datalake';
import type { ThemeModel } from './theme';
import { themeModel } from './theme';
import type { LimitModel } from './limit';
import { limitModel } from './limit';
import type { QzTrayModel } from './qzTray';
import { qzTrayModel } from './qzTray';
import type { BarcodeScanModel } from './barcodeScan';
import { barcodeScanModel } from './barcodeScan';
import type { TagModel } from './assets/tags';
import { tagModel } from './assets/tags';
import type { LabelAugmentationModel } from './assets/label-augmentations';
import { labelAugmentationModel } from './assets/label-augmentations';
import type { CarrierAccountModel } from './assets/carrier-accounts';
import { carrierAccountModel } from './assets/carrier-accounts';
import type { OriginModel } from './assets/origins';
import { originModel } from './assets/origins';
import type { WorkflowModel } from './workflow';
import { workflowModel } from './workflow';
import type { CarrierAssetModel } from './assets/carriers';
import { carrierAssetModel } from './assets/carriers';
import type { AddressModel } from './assets/addresses';
import { addressModel } from './assets/addresses';
import type { ContactModel } from './assets/contacts';
import { contactModel } from './assets/contacts';
import type { ScheduleModel } from './assets/schedule';
import { scheduleModel } from './assets/schedule';
import type { CarrierSettingsModel } from './assets/carrierSettings';
import { carrierSettingsModel } from './assets/carrierSettings';
import type { ContractModel } from './assets/contracts';
import { contractModel } from './assets/contracts';
import type { FulfillmentContextModel } from './assets/fulfillmentContext';
import { fulfillmentContextModel } from './assets/fulfillmentContext';
import type { FulfillmentContextLinkPropertiesModel } from './assets/fulfillmentContextLinkProperties';
import { fulfillmentContextLinkModel } from './assets/fulfillmentContextLinkProperties';
import type { LabelPropertiesModel } from './assets/labelProperties';
import { labelPropertiesModel } from './assets/labelProperties';
import type { PackagingModel } from './assets/packaging';
import { packagingModel } from './assets/packaging';
import type { ReturnsSettingsModel } from './assets/returnsSettings';
import { returnsSettingsModel } from './assets/returnsSettings';
import type { TenantModel } from './assets/tenants';
import { tenantModel } from './assets/tenants';
import type { PartnerConfigurationModel } from './partnerConfiguration/index';
import { partnerConfigurationModel } from './partnerConfiguration/index';
import type { FP_FulfillmentContextModel } from './fulfillmentContext';
import { FP_fulfillmentContextModel } from './fulfillmentContext';
import type { ThirdPartyBillingAccountModel } from './assets/thirdPartyBillingAccount';
import { thirdPartyBillingAccountModel } from './assets/thirdPartyBillingAccount';
import type { ZipZoneModel } from './zip-zone';
import { zipZoneModel } from './zip-zone';
import type { ConfigurationAssetsModel } from './configurationAssets';
import { configurationAssetsModel } from './configurationAssets';
import type { SimulationModel } from './simulation';
import { simulationModel } from './simulation';
import type { ManifestModel } from './manifest';
import { manifestModel } from './manifest';
import type { TimeInTransitModel } from './timeInTransit';
import { timeInTransitModel } from './timeInTransit';
import type { LedgerModel } from './ledger';
import { ledgerModel } from './ledger';
import type { PackagingModelV2 } from './packaging';
import { packagingModelV2 } from './packaging';
import type { FulfillmentEngineModel } from './fulfillmentEngine';
import { fulfillmentEngineModel } from './fulfillmentEngine';
import type { ReturnToAddressOverrideModel } from './assets/returnToAddressOverrides';
import { returnToAddressOverrideModel } from './assets/returnToAddressOverrides';
import type { ShipmentLaneModel } from './assets/shipmentLanes';
import { shipmentLanesModel } from './assets/shipmentLanes';

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
});

const middleware: any = [routerMiddleware, checkRefreshing];

if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
  middleware.push(createLogger());
}

export interface StoreModel {
  errors: ErrorModel;
  session: SessionModel;
  navigation: NavigationModel;
  queryInvalidation: QueryInvalidationModel;
  loading: LoadingModel;
  messages: MessagesModel;
  helper: HelperModel;
  siteReporting: SiteReportingModel;
  fulfillmentCenterConfig: FulfillmentModel;
  i18n: I18nModel;
  partner: PartnerModel;
  router: any;
  users: UserModel;
  serviceStatus: ServiceStatusModel;
  file: FileModel;
  tracking: TrackingModel;
  carrier: CarrierModel;
  security: SecurityModel;
  zpl: ZplModel;
  shipments: ShipmentsModel;
  dataFetching: DataFetchingModel;
  theme: ThemeModel;
  limit: LimitModel;
  qztray: QzTrayModel;
  barcodeScan: BarcodeScanModel;
  tags: TagModel;
  labelAugmentations: LabelAugmentationModel;
  carrierAccounts: CarrierAccountModel;
  origins: OriginModel;
  workflow: WorkflowModel;
  carrierAssets: CarrierAssetModel;
  address: AddressModel;
  contact: ContactModel;
  schedule: ScheduleModel;
  carrierSettings: CarrierSettingsModel;
  contract: ContractModel;
  fulfillmentContext: FulfillmentContextModel;
  fulfillmentContextLinkProperties: FulfillmentContextLinkPropertiesModel;
  labelProperties: LabelPropertiesModel;
  packaging: PackagingModel;
  packagingV2: PackagingModelV2;
  returnsSettings: ReturnsSettingsModel;
  tenant: TenantModel;
  partnerConfiguration: PartnerConfigurationModel;
  FP_fulfillmentContext: FP_FulfillmentContextModel;
  thirdPartyBilling: ThirdPartyBillingAccountModel;
  zipZone: ZipZoneModel;
  configurationAssets: ConfigurationAssetsModel;
  simulation: SimulationModel;
  manifest: ManifestModel;
  timeInTransit: TimeInTransitModel;
  ledgerModel: LedgerModel;
  datalake: DatalakeModel;
  fulfillmentEngine: FulfillmentEngineModel;
  returnToAddressOverrides: ReturnToAddressOverrideModel;
  lanes: ShipmentLaneModel;
}

export const storeModel: StoreModel = {
  file: fileModel,
  users: userModel,
  session: sessionModel,
  navigation: navigationModel,
  queryInvalidation: queryInvalidationModel,
  loading: loadingModel,
  errors: errorModel,
  messages: messagesModel,
  carrier: carrierModel,
  helper: helperModel,
  siteReporting: siteReportingModel,
  fulfillmentCenterConfig: fulfillmentModel,
  i18n: i18nModel,
  partner: partnerModel,
  serviceStatus: serviceStatusModel,
  security: securityModel,
  tracking: trackingModel,
  zpl: zplModel,
  shipments: shipmentsModel,
  dataFetching: dataFetchingModel,
  limit: limitModel,
  theme: themeModel,
  qztray: qzTrayModel,
  barcodeScan: barcodeScanModel,
  tags: tagModel,
  labelAugmentations: labelAugmentationModel,
  carrierAccounts: carrierAccountModel,
  carrierAssets: carrierAssetModel,
  origins: originModel,
  workflow: workflowModel,
  address: addressModel,
  contact: contactModel,
  schedule: scheduleModel,
  carrierSettings: carrierSettingsModel,
  contract: contractModel,
  fulfillmentContext: fulfillmentContextModel,
  fulfillmentContextLinkProperties: fulfillmentContextLinkModel,
  labelProperties: labelPropertiesModel,
  packaging: packagingModel,
  packagingV2: packagingModelV2,
  returnsSettings: returnsSettingsModel,
  returnToAddressOverrides: returnToAddressOverrideModel,
  lanes: shipmentLanesModel,
  tenant: tenantModel,
  partnerConfiguration: partnerConfigurationModel,
  FP_fulfillmentContext: FP_fulfillmentContextModel,
  thirdPartyBilling: thirdPartyBillingAccountModel,
  zipZone: zipZoneModel,
  configurationAssets: configurationAssetsModel,
  simulation: simulationModel,
  manifest: manifestModel,
  timeInTransit: timeInTransitModel,
  ledgerModel: ledgerModel,
  datalake: datalakeModel,
  fulfillmentEngine: fulfillmentEngineModel,
  router: reducer<RouterState>(routerReducer),
};

export const store = createStore(storeModel, {
  middleware,
  injections,
});

export const { useStore, useStoreActions, useStoreDispatch, useStoreState } = createTypedHooks<StoreModel>();
export const history = createReduxHistory(store);
export const reachHistory = reachify(history);
